import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';

import { MessagesActions } from './messages.actions';

import {
  MessageMetaService,
  MessageService,
  MessageThreadService,
} from '@univacity/angular/datas';
import { ApiError } from '@univacity/shared/types';
import {
  catchError,
  map,
  mergeMap,
  of,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs';
import { MessagesFacade } from './messages.facade';

@Injectable()
export class MessagesEffects {
  private actions$ = inject(Actions);
  constructor(
    private thrreadService: MessageThreadService,
    private meta: MessageMetaService,
    private message: MessageService,
    private facade: MessagesFacade
  ) {}

  fetchThread$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MessagesActions.fetchThreads),
      switchMap(({ query }) => this.thrreadService.find(query)),
      tap((res) =>
        res.docs.forEach((t) => this.facade.fetchThreadUnread(t._id))
      ),
      map((res) => MessagesActions.fetchThreadsSuccess({ res })),
      catchError((error: ApiError) => {
        console.error('Error', error);
        return of(MessagesActions.fetchThreadsFailure({ error }));
      })
    )
  );

  fetchUnreadMessagesCount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MessagesActions.fetchUnreadMessagesCount),
      mergeMap(({ thread }) =>
        this.meta
          .countThreadUnread(thread)
          .pipe(
            map((count) =>
              MessagesActions.fetchUnreadMessagesCountSuccess({ count, thread })
            )
          )
      ),
      catchError((error: ApiError) => {
        console.error('Error', error);
        return of(MessagesActions.fetchUnreadMessagesCountFailure({ error }));
      })
    )
  );

  fetchAllUnreadMessagesCount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MessagesActions.fetchAllUnreadMessagesCount),
      switchMap(() => this.meta.countUserUnread()),
      map((count) =>
        MessagesActions.fetchAllUnreadMessagesCountSuccess({ count })
      ),
      catchError((error: ApiError) => {
        console.error('Error', error);
        return of(
          MessagesActions.fetchAllUnreadMessagesCountFailure({ error })
        );
      })
    )
  );

  selectThread$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(MessagesActions.selectThread),
        map(({ thread }) => {
          if (thread) this.facade.fetchMessages(thread);
        })
      ),
    { dispatch: false }
  );

  fetchMessages$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MessagesActions.fetchMessages),
      switchMap(({ thread }) =>
        this.message
          .find(thread)
          .pipe(
            map((res) => MessagesActions.fetchMessagesSuccess({ res, thread }))
          )
      ),
      catchError((error: ApiError) => {
        console.error('Error', error);
        return of(MessagesActions.fetchMessagesFailure({ error }));
      })
    )
  );

  loadMoreMessages$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MessagesActions.loadMoreMessages),
      withLatestFrom(
        this.facade.messagePagination$,
        this.facade.selectedThread$
      ),
      switchMap(([_, paginate, thread]) => {
        if (paginate?.nextPage && thread)
          return this.message
            .find(thread._id, { page: paginate.nextPage })
            .pipe(
              map((res) =>
                MessagesActions.fetchMessagesSuccess({
                  res,
                  thread: thread._id,
                })
              )
            );
        return of(MessagesActions.loadMoreMessagesEmpty());
      }),
      catchError((error: ApiError) => {
        console.error('Error', error);
        return of(MessagesActions.fetchMessagesFailure({ error }));
      })
    )
  );

  sendMessage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MessagesActions.sendMessage),
      switchMap(({ thread, message }) => this.message.create(thread, message)),
      map((message) => MessagesActions.sendMessageSuccess({ message })),
      catchError((error: ApiError) => {
        console.error('Error', error);
        return of(MessagesActions.sendMessageFailure({ error }));
      })
    )
  );
}
